import React, { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Pencil, Trash2, Plus } from "lucide-react";

const EMPTY = { id: null, title: "", text: "" };

export function ManageRepliesDialog({ open, onOpenChange, replies, reload }) {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      if (form.id) await api.put(`/support/replies/${form.id}`, { title: form.title, text: form.text });
      else await api.post("/support/replies", { title: form.title, text: form.text });
      toast.success(form.id ? "تم تعديل الرد" : "تمت إضافة الرد");
      setForm(EMPTY); reload();
    } catch (ex) {
      toast.error(formatApiError(ex.response?.data?.detail));
    }
    setBusy(false);
  };
  const remove = async (r) => {
    if (!window.confirm(`حذف الرد «${r.title}»؟`)) return;
    try {
      await api.delete(`/support/replies/${r.id}`);
      toast.success("تم حذف الرد");
    } catch (ex) {
      toast.error(formatApiError(ex.response?.data?.detail));
    }
    if (form.id === r.id) setForm(EMPTY);
    reload();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent dir="rtl" className="max-w-lg" data-testid="manage-replies-dialog">
        <DialogHeader><DialogTitle>الردود الجاهزة</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-2 bg-muted/40 rounded-xl p-3">
          <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} maxLength={60} placeholder="عنوان الرد" data-testid="reply-title-input" />
          <Textarea value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} rows={3} maxLength={2000} placeholder="نص الرد — اكتب {الاسم} ليُستبدل باسم صاحب التذكرة" data-testid="reply-text-input" />
          <div className="flex gap-2">
            <Button type="submit" size="sm" className="rounded-full gap-1" disabled={busy || form.title.trim().length < 2 || form.text.trim().length < 2} data-testid="reply-save-btn">
              <Plus className="w-4 h-4" /> {form.id ? "حفظ التعديل" : "إضافة رد"}
            </Button>
            {form.id && <Button type="button" size="sm" variant="ghost" onClick={() => setForm(EMPTY)} data-testid="reply-cancel-edit">إلغاء</Button>}
          </div>
        </form>
        <div className="max-h-72 overflow-y-auto divide-y divide-border">
          {replies.map((r) => (
            <div key={r.id} className="py-2 flex items-start gap-2" data-testid={`reply-row-${r.id}`}>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold">{r.title}</div>
                <div className="text-xs text-muted-foreground whitespace-pre-line">{r.text}</div>
              </div>
              <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setForm({ id: r.id, title: r.title, text: r.text })} data-testid={`reply-edit-${r.id}`}><Pencil className="w-3.5 h-3.5" /></Button>
              <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive" onClick={() => remove(r)} data-testid={`reply-delete-${r.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
